import { Component, Input, OnInit } from '@angular/core';
import { environment } from '@environments/environment';
import { INDRABookedTripDetails } from '@ibe.models/booked-trip-details.indra.model.lib';
import { INDRAMybService } from '@ibe.services/myb.indra.service';
import { INDRADateService } from '@ibe.services/date.indra.service';
import { INDRAIbeAdminService } from '@ibe.services/ibeadmin.indra.service';
import { Observable } from 'rxjs';
import { INDRABFMDataStorageService } from '@ibe.services/bfm-data-storage.indra.service';
import { INDRABookingSearchService } from '@ibe.services/booking-search.indra.service';
import { TP_EXPRESS_LOGO } from '@ibe.config';

@Component({
	selector: 'app-timeline',
	templateUrl: './timeline.indra.component.html',
	styleUrls: ['./timeline.component.scss']
})
export class INDRATimelineComponent implements OnInit {
	@Input() booked_trip: INDRABookedTripDetails;
	timelineSteps = [];

	constructor(private mybService: INDRAMybService,
		private dateService: INDRADateService,
		protected ibeAdminService: INDRAIbeAdminService,
		private bookingSearchService: INDRABookingSearchService
		) {}

	INDRA_formatDate(time) {
		return this.dateService.INDRA_formatDate(time, 'MMM D');
	}

	INDRA_formatDateInterval(timeStart, stopTime) {
		const end = this.dateService.INDRA_add(timeStart, stopTime, 'minutes');
		return (
			this.INDRA_formatDate(timeStart) + ' - ' + this.INDRA_formatDate(end)
		);
	}

	INDRA_formatDaysCount(minutes: number) {
		const daysCount = this.mybService.INDRA_getEquivalentDaysOfMins(minutes);
		return daysCount;
	}
	
	INDRA_getStopOverDaysKey(daysCount: number): string {
		// Check if plural or singular
		if (daysCount === 1) {
			return 'bookingDetails.label.stopOverDay';
		} else {
			return 'bookingDetails.label.stopOverDays';
		}
	}
	
	INDRA_printAirlineLogo(airLines : string[]) {
		let airlineLogo : string;

		if (airLines.length > 1) {
			airlineLogo = 'Multiple';
		} else {

			airlineLogo = airLines[0];
		}
		return airlineLogo;
	}
	
	INDRA_printAirlineName(airLines : string[]) {
		let airlineName : string;

		if (airLines.length === 1) {
			airlineName = this.booked_trip.translate.airlines[airLines[0]].name;
		}
		return airlineName;
	}

	INDRA_getTranslation(key: string) : string {
		return this.ibeAdminService.getTranslationValue(key);
	}

	INDRA_getTranslationAsync(key: string): Observable<string> {
		return this.ibeAdminService.getTranslationValueAsync(key);
	}

	ngOnInit() {
		// outbound flights
		let airlineLogoOutbound : string;
		let airlineLogoInbound: string;
		let airlineNameOutbound : string;
		let airlineNameInbound: string;
		this.booked_trip.data.fare.listOutbound.forEach((outbound, index) => {
			const outboundLength = outbound.listSegment.length;
			const outboundAirlines: string[] = [];
			outbound.listSegment.forEach(segment => {
				if (!outboundAirlines.includes(segment.operationCarrier)) {
					outboundAirlines.push(segment.operationCarrier);
				}

			});

			airlineLogoOutbound = this.INDRA_printAirlineLogo(outboundAirlines);
			airlineNameOutbound = this.INDRA_printAirlineName(outboundAirlines);

			this.timelineSteps.push({
				label:
					outbound.listSegment[0].departureAirport +
					' - ' +
					outbound.listSegment[outboundLength - 1].arrivalAirport,
				logo: this.bookingSearchService.INDRA_buildAirlineLogoName(airlineLogoOutbound),
				multipleAirlinesFlag: (outboundAirlines.length > 1 ? true : false),
				tooltip: airlineNameOutbound,
				date: this.INDRA_formatDate(outbound.listSegment[0].departureDate),
				class: 'timeline__step--flight'
			});

			if (outbound.isStopOver && !outbound.isLongLayover) {
        const nextOutbound = this.booked_trip.data.fare.listOutbound[index + 1];
				this.timelineSteps.push({
					label: this.booked_trip.translate.locations[outbound.listSegment[outboundLength - 1].arrivalAirport].city,
					logo: 'icons.svg#services--hotel',
					tooltip: this.booked_trip.translate.locations[outbound.listSegment[outboundLength - 1].arrivalAirport].city,
				  date: `${this.INDRA_formatDate(outbound.listSegment[0].departureDate)} - ${this.INDRA_formatDate(nextOutbound.listSegment[0].departureDate)}`,
					descriptionCount: this.INDRA_formatDaysCount(outbound.listSegment[outboundLength - 1].stopTime),
					descriptionSuffix: this.INDRA_getStopOverDaysKey(this.INDRA_formatDaysCount(outbound.listSegment[outboundLength - 1].stopTime)),
					class: 'timeline__step--stopover'
				});
			}
		});

		// inbound flight
		if (this.booked_trip.data.fare.listInbound) {
			this.booked_trip.data.fare.listInbound.forEach((inbound, index) => {
				const inboundAirLines : string [] = [];

				const inboundLength = inbound.listSegment.length;
				inbound.listSegment.forEach(segment => {
					if (!inboundAirLines.includes(segment.operationCarrier)) {
						inboundAirLines.push(segment.operationCarrier);
					}
				});

				airlineLogoInbound = this.INDRA_printAirlineLogo(inboundAirLines);
				airlineNameInbound = this.INDRA_printAirlineName(inboundAirLines);

				this.timelineSteps.push({
					label:
						inbound.listSegment[0].departureAirport +
						' - ' +
						inbound.listSegment[inboundLength - 1].arrivalAirport,
					logo: this.bookingSearchService.INDRA_buildAirlineLogoName(airlineLogoInbound),
					multipleAirlinesFlag: (inboundAirLines.length > 1 ? true : false),
					tooltip: airlineNameInbound,
					date: this.INDRA_formatDate(inbound.listSegment[0].departureDate),
					class: 'timeline__step--flight'
				});

				if (inbound.isStopOver && !inbound.isLongLayover) {
          const nextInbound = this.booked_trip.data.fare.listInbound[index + 1];
					this.timelineSteps.push({
						label: this.booked_trip.translate.locations[inbound.listSegment[inboundLength - 1].arrivalAirport].city,
						logo: 'icons.svg#services--hotel',
						tooltip: this.booked_trip.translate.locations[inbound.listSegment[inboundLength - 1].arrivalAirport].city,
				    date: `${this.INDRA_formatDate(inbound.listSegment[0].departureDate)} - ${this.INDRA_formatDate(nextInbound.listSegment[0].departureDate)}`,
						descriptionCount: this.INDRA_formatDaysCount(inbound.listSegment[inboundLength - 1].stopTime),
						descriptionSuffix: this.INDRA_getStopOverDaysKey(this.INDRA_formatDaysCount(inbound.listSegment[inboundLength - 1].stopTime)),
						class: 'timeline__step--stopover'
					});
				}
			});
		}
	}
	
	INDRA_buildAirlineLogoName(carrier) {
		return 'sprite.svg#airlines--' + (TP_EXPRESS_LOGO.includes(carrier) ? 'TP_express' : carrier);
	}
	INDRA_cityLabel(city: string) {
		if (city.toLowerCase() === 'lisbon') {
			return this.ibeAdminService.getTranslationValue('extrasSidebar.text.countryLisbon');
		}
		return city;
	}
}
